import React, { useReducer, useState } from 'react';
import { t } from 'i18n';

import ActionButton from 'Components/Base/ActionButton';
import FormField from 'Components/Forms/FormField';
import Checkbox from 'Components/Forms/Checkbox';
import QuillEditor from 'Components/Base/Quilljs/QuillEditor';
import CarePlanTemplatesSelect from './CarePlanTemplatesSelect';
import VisibilitySelect from './VisibilitySelect';
import savePlaceContactWrapper from './savePlaceContactWrapper';
import {
  setupOptionalEmailValidator,
  setupCustomTitleValidator,
  setupPhoneOrEmailValidator,
  setupContactTypeValidator,
} from './formValidators';

const DEFAULT_VISIBILITY = {
  patientsAndSupportTeams: true,
  yourHealthTeam: true,
  invitedHealthTeams: true,
};

const i18nOpts = { scope: 'place_contacts.admin_panel.form' };

const formErrorsReducer = (state, action) => {
  switch (action.type) {
    case 'merge':
      return { ...state, ...action.patch };
    default:
      return state;
  }
};

const contactTypesOptions = [
  'daytime_non_urgent',
  'daytime_urgent',
  'nursing_line_non_urgent',
  'nursing_line_urgent',
  'on_call',
  'reception',
  'custom'
];

const ContactTypeSelectField = ({
  contactType,
  setContactType,
  showBlankOption,
  formErrors,
  onContactTypeChange,
}) => {
  return (
    <div className={`contact_type_container ${formErrors?.contactType ? 'input--errors' : ''}`}>
      <label htmlFor="contactType">{t('contact_type', i18nOpts)}</label>
      <select
        name="contactType"
        id="contactType"
        className="select-container__select"
        value={contactType}
        onChange={({ target: { value } }) => {
          setContactType(value);
          onContactTypeChange?.(value);
        }}
      >
        {showBlankOption && <option value="">{''}</option>}
        {contactTypesOptions.map((option) => {
          return <option key={`contactType-${option}`} value={option}>{t(option, i18nOpts)}</option>
        })}
      </select>
      {formErrors?.contactType && <div className="error">{formErrors.contactType}</div>}
    </div>
  );
}

const computeFrozenFormHeader = (mode, placeContact) => {
  const formInEditMode = mode === 'edit';
  
  let header = t('new_contact', i18nOpts);
  
  if (!formInEditMode) { return header; }

  const initialContactType = placeContact?.contactType;
  const initialTitle = placeContact?.title;

  header = `${t('edit_contact', i18nOpts)} `;

  // if contact type is custom - display it's name (title)
  // if contact type is not custom - display type of contact
  if (initialContactType === 'custom') {
    header += initialTitle ? initialTitle : t(initialContactType, i18nOpts);
  } else {
    header += t(initialContactType, i18nOpts);
  }
  
  return header;
}

const EditForm = ({
  mode,
  placeContact,
  carePlanTemplates,
  placeName
}) => {
  const formInEditMode = mode === 'edit';
  const [formHeader] = useState(() => computeFrozenFormHeader(mode, placeContact));

  const availableCarePlanTemplates = carePlanTemplates || [];

  const [formErrors, dispatchFormErrors] = useReducer(formErrorsReducer, {});
  const setFormErrors = (patch) => dispatchFormErrors({ type: 'merge', patch });
  const [contactType, setContactType] = useState(formInEditMode ? (placeContact?.contactType || '') : '');
  const [title, setTitle] = useState(placeContact?.title || '');
  const [email, setEmail] = useState(placeContact?.email || '');
  const [phone, setPhone] = useState(placeContact?.phone || '');
  const [extension, setExtension] = useState(placeContact?.extension || '');
  const [instructions, setInstructions] = useState(placeContact?.instructions || []);
  const [universal, setUniversal] = useState(placeContact?.universal);
  const [selectedCarePlanTemplateIds, setSelectedCarePlanTemplateIds] = useState(placeContact?.carePlanTemplateIds || []);
  const [visibility, setVisibility] = useState({
    ...DEFAULT_VISIBILITY,
    ...(placeContact?.visibility || {}),
  });
  const [isLoading, setIsLoading] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const optionalEmailValidator = setupOptionalEmailValidator(setFormErrors, t, i18nOpts);
  const contactTypeValidator = setupContactTypeValidator(setFormErrors, t, i18nOpts);
  const customTitleValidator = setupCustomTitleValidator(setFormErrors, t, i18nOpts);
  const phoneOrEmailValidator = setupPhoneOrEmailValidator(setFormErrors, t, i18nOpts);

  const contactTitleRequired = contactType === 'custom';
  const showBlankContactTypeOption = !formInEditMode && !contactType;

  const runAllValidators = () => {
    const isContactTypeValid = contactTypeValidator(contactType);
    const isEmailValid = optionalEmailValidator(email);
    const isTitleValid = customTitleValidator(title, contactType);
    const isPhoneOrEmailValid = phoneOrEmailValidator(email, phone);

    return isContactTypeValid && isEmailValid && isTitleValid && isPhoneOrEmailValid;
  };

  const save = savePlaceContactWrapper();
  const savePlaceContact = () => {
    const validationFailed = !runAllValidators();
    if (validationFailed) return;

    save(
      {
        mode,
        id: placeContact?.id,
        contactType,
        title,
        email,
        phone,
        extension,
        instructions,
        universal,
        visibility,
        carePlanTemplateIds: selectedCarePlanTemplateIds,
      },
      { setIsLoading, setSaveError }
    );
  };

  return (
    <div className="card place_contact_form">
      <h3 className="card__header">{formHeader}</h3>
      <div className="form__row">
        <ContactTypeSelectField
          contactType={contactType}
          setContactType={setContactType}
          showBlankOption={showBlankContactTypeOption}
          formErrors={formErrors}
          onContactTypeChange={contactTypeValidator}
        />
        {contactTitleRequired && (
          <FormField
            className="input custom_title"
            label={t('title', i18nOpts)}
            name="title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            formErrors={formErrors}
            handleValidation={(value) => customTitleValidator(value, contactType)}
          />
        )}
      </div>
      <div className="form__row">
        <FormField
          className="input contact_input"
          label={t('email', i18nOpts)}
          name="email"
          value={email}
          formErrors={formErrors}
          onChange={({ target: { value } }) => setEmail(value)}
          handleValidation={optionalEmailValidator}
        />
        <FormField
          className="input contact_input"
          label={t('phone', i18nOpts)}
          name="phone"
          value={phone}
          formErrors={formErrors}
          onChange={({ target: { value } }) => setPhone(value)}
        />
        <FormField
          className="input"
          label={t('extension', i18nOpts)}
          name="extension"
          value={extension}
          onChange={({ target: { value } }) => setExtension(value)}
        />
      </div>
      <div className="form__row instructions_container">
        <label className="instructions_label">{t('instructions', i18nOpts)}</label>
        <div className="instructions-editor-section">
          <QuillEditor
            contentFormatted={instructions}
            setContentFormatted={setInstructions}
          />
        </div>
      </div>
      <div className="form__row universal_container">
        <Checkbox
          checked={universal}
          name="universalCheckbox"
          text={t('universal', i18nOpts)}
          onChange={() => setUniversal(!universal)}
        />
      </div>
      {!universal && (
        <div className="form__row">
          <CarePlanTemplatesSelect
            carePlanTemplates={availableCarePlanTemplates}
            selectedIds={selectedCarePlanTemplateIds}
            setSelectedIds={setSelectedCarePlanTemplateIds}
          />
        </div>
      )}
      <div className="form__row">
        <VisibilitySelect
          visibility={visibility}
          setVisibility={setVisibility}
          yourHealthTeamName={placeName}
        />
      </div>
      {saveError && (
        <p className="form__row save_error">{t('save_error', i18nOpts)}</p>
      )}
      <div className="buttons_section">
        <button
          type="button"
          className="buttonV2 button-flat"
          onClick={() => { window?.location.assign('/place_contacts'); }}
          disabled={isLoading}
        >
          {t('cancel', i18nOpts)}
        </button>
        <ActionButton
          content={t('save', i18nOpts)}
          onClick={savePlaceContact}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default EditForm;